import React, { useEffect, useState, useRef, MutableRefObject } from "react";
import "./LogViewer.css";
import ReactLoading from "react-loading";
import Clipboard from "clipboard";
import { useSelector, useDispatch } from 'react-redux';
import { updateHoverIndex } from "../../store/features/hoverIndexSlice";
import { checkIsLink, deepClone, hexEncode, animation } from "../../utils/helpers";
import { DivRef, DivArrRef } from "../../utils/typesAndMaps";
import { RootStore } from "../../store";
import { ResultItem } from "../../store/features/responseSlice";

interface LogViewerProps {
  logPreRef: MutableRefObject<Array<HTMLPreElement>>
}

export default function LogViewer({ logPreRef }: LogViewerProps) {
  const logRef: DivRef = useRef(null);
  const barcodeLogRef: DivArrRef = useRef([]);

  const [isShowDefaultLog, setDefaultLogVisibility] = useState(true);

  const globalSettings = useSelector((store: RootStore) => store.globalSettings);
  const imagesData = useSelector((store: RootStore) => store.imagesData);
  const hoverIndex = useSelector((store: RootStore) => store.hoverIndex);
  const response = useSelector((store: RootStore) => store.decodeResponse);
  const generalStates = useSelector((store: RootStore) => store.generalStates);
  const exLog = useSelector((store: RootStore) => store.exLog);
  const dispatch = useDispatch();

  useEffect(() => {
    if (JSON.stringify(response) !== "{}") {
      setDefaultLogVisibility(false);
    }
  }, [response]);

  useEffect(() => {
    setDefaultLogVisibility(true);
  }, [generalStates.curImgIdx]);

  useEffect(() => {
    if (!logRef.current) return;
    if (!barcodeLogRef.current![hoverIndex.index]) return;
    if (hoverIndex.isChangeCvsStyle || hoverIndex.index === -1) return;
    const logMain = logRef.current;
    const logRect = logMain!.getBoundingClientRect();
    const barcodeLog = barcodeLogRef.current[hoverIndex.index].getBoundingClientRect();
    const scrollTop = logMain.scrollTop + barcodeLog.top - logRect.height / 2;
    logMain!.scrollTop = scrollTop;
  }, [hoverIndex]);

  const openResult = (index: number) => {
    animation(logPreRef.current[index], 2);
  }

  const renderResultInfo = () => {
    return response.results?.map((item: ResultItem, index: number) => {
      const result = deepClone(item);
      delete result.Results;
      delete result.DetailedResult;
      result.bytes = hexEncode(atob(result.bytes));
      const isLink = checkIsLink(result.text);
      return (
        <div
          className="dbr-barcode-log"
          key={index}
          style={{ backgroundColor: hoverIndex.isChangeLogStyle && hoverIndex.index === index ? "rgba(43,144,203,0.7)" : "" }}
          onMouseMove={() => { dispatch(updateHoverIndex({ value: index, isChangeCvsStyle: true, isChangeLogStyle: false })) }}
          onMouseLeave={() => { dispatch(updateHoverIndex({ value: -1, isChangeCvsStyle: true, isChangeLogStyle: false })) }}
          ref={(node: HTMLDivElement) => { barcodeLogRef.current[index] = node }}
        >
          <button onClick={() => { openResult(index) }}></button>
          <label onClick={() => { openResult(index) }}>{result.formatString}</label>&nbsp;
          {isLink ? <a href={`https://${result.text.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" style={{ color: "#2ba6cb" }}>{result.text}</a> : <span>{result.text}</span>}
          <pre ref={(node: HTMLPreElement) => { logPreRef.current[index] = node }} style={{ backgroundColor: "#F5F5F5" }}>
            {JSON.stringify(result, (_: any, value: any) => {
              if (typeof value === 'string') {
                return value.replace(/\n/g, '\\n');
              }
              return value;
            }, 2)}
          </pre>
        </div>
      )
    })
  }

  const renderExLog = () => {
    return exLog.map((item, index) => {
      return (
        <div className="dbr-ex-log" key={index} style={{ color: item.isError ? "#900" : "black" }}>{item.content}</div>
      )
    })
  }

  const copyLog = () => {
    const clipboard = new Clipboard(".dbr-header-copy svg", {
      text() {
        return logRef.current!.innerText;
      }
    });
    clipboard.on("success", () => {
      //alert("Copied!");
      clipboard.destroy();
    })
    clipboard.on("error", () => {
      alert("Failed!");
      clipboard.destroy();
    })
  }

  return (
    <div className="dbr-log">
      <div className="dbr-header-copy">
        <svg width="16" height="18" viewBox="0 0 16 18" xmlns="http://www.w3.org/2000/svg" onClick={copyLog}>
          <path fill="#ddd" d="M6394,219h-10.32a1.666,1.666,0,0,0-1.68,1.636V232h2V221h10v-2Zm-1,3h-6a2.3,2.3,0,0,0-2,2v11a2.355,2.355,0,0,0,2.04,2h9.28a1.666,1.666,0,0,0,1.68-1.636v-8.182Zm-6,13V224l5.11-.091V228H6396v7h-9Z" transform="translate(-6382 -219)"></path>
        </svg>
      </div>
      <div className="dbr-main-log" ref={logRef}>
        {
          isShowDefaultLog ?
            <div className="dbr-default-log">
              With <a href="https://www.dynamsoft.com/barcode-reader/overview/" target="_blank" rel="noreferrer">Dynamsoft Barcode Reader SDK</a>, developers can easily integrate barcode detection and decoding functionalities into their desktop, web and mobile applications. Barcode scanning SDK with multiple packages for programming with JavaScript, C/C++, .NET, Python, Java, iOS, Android.
            </div>
            :
            <div className="dbr-log-content">
              <div>Cost {response.time} ms.</div>
              <div className="dbr-log-second-line">Found <strong>{response.results?.length}</strong> barcode(s) in this file. {generalStates.loadingContent === "Second attempt at decoding" && <strong>at first attempt.</strong>}</div>
              {
                !imagesData[generalStates.curImgIdx].sampleImg && response.results && response.results.length < globalSettings.ExpectedBarcodesCount.value && response.results.length < 20 &&
                <strong>Having trouble scanning all the barcodes in the image? Please <a href="https://www.dynamsoft.com/contact/" target="blank" style={{color: "#2ba6cb"}}>contact our support team</a> for assistance. </strong>
              }
              {
                !imagesData[generalStates.curImgIdx].sampleImg && response.results && response.results.length >= 20 && 
                <strong>
                  You've successfully decoded over 20 barcodes. For businesses who need to scan large batches of inventory and have zero tolerance for inaccuracy, we recommend using our batch scan solution.
                  <a href="https://www.dynamsoft.com/panorama/batch-barcode-scanning/." target="blank" style={{color: "#2ba6cb"}}> Learn more about our batch scan solution</a>
                </strong>
              }
              {
                generalStates.loadingContent === "Second attempt at decoding" &&
                <div className="dbr-log-third-line"><strong>Second attempt at decoding</strong><ReactLoading type="bubbles" className="dbr-text-loading" width="15px" height="15px"></ReactLoading></div>
              }
              {renderResultInfo()}
              <div style={{ marginTop: "15px" }}>This demo is built with Dynamsoft Barcode Reader SDK.</div>
            </div>
        }
        <a className="dbr-get-free-trial" href="https://www.dynamsoft.com/barcode-reader/downloads/#desktop" target="_blank" rel="noreferrer">GET FREE TRIAL &gt;</a>
        <a className="dbr-view-more-samples" href="https://github.com/Dynamsoft#barcode-reading" target="_blank" rel="noreferrer">VIEW MORE SAMPLES &gt;</a>
        {renderExLog()}
      </div>
    </div>
  );
}